import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import apiClient from "utils/apiClient";
import { useAuth } from "contexts/AuthContext";

const useUsers = (page = 0, pageSize = 20, initialStaff = null, includeStats = true, options = {}) => {
  const authToken = sessionStorage.getItem("authToken");
  const { user } = useAuth();
  const autoFetch = options?.autoFetch !== false;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [staffFilter, setStaffFilter] = useState(initialStaff); // Initialize with prop

  // Update staffFilter when initialStaff changes
  useEffect(() => {
    setStaffFilter(initialStaff);
  }, [initialStaff]);

  /** -------------------- Generic Helpers -------------------- **/

  const unwrapContentObject = (data) =>
    data?.content && !Array.isArray(data.content) ? data.content : data;

  const identityTimestampsCacheRef = useRef(new Map());
  const identityListFetchRef = useRef({ inFlight: null, fetchedAll: false });

  const extractIdentityTimestamps = (identityResponse) => {
    const identityUser = unwrapContentObject(identityResponse);
    return {
      createdAt:
        identityUser?.createdAt ||
        identityUser?.createdOn ||
        identityUser?.insertedAt ||
        null,
      updatedAt:
        identityUser?.updatedAt ||
        identityUser?.updatedOn ||
        identityUser?.modifiedAt ||
        identityUser?.lastUpdatedAt ||
        null,
    };
  };

  const enrichUsersWithIdentityTimestamps = useCallback(async (userList = []) => {
    const validUsers = (Array.isArray(userList) ? userList : []).filter(
      (u) => u?.id && u.id !== "None",
    );

    if (validUsers.length === 0) return userList;
    // Avoid exploding network calls (stats fetch can be huge).
    if (validUsers.length > 100) return userList;

    const prefetchFromIdentityList = async () => {
      if (identityListFetchRef.current.fetchedAll) return;
      if (identityListFetchRef.current.inFlight) {
        await identityListFetchRef.current.inFlight;
        return;
      }

      identityListFetchRef.current.inFlight = (async () => {
        try {
          const pageSize = 500;
          let page = 0;
          let totalPages = 1;

          while (page < totalPages) {
            const res = await apiClient.get(
              `/identity-service/api/v1/users?page=${page}&size=${pageSize}`,
            );
            const payload = res?.data || {};
            const content = Array.isArray(payload?.content) ? payload.content : [];
            totalPages = Number(payload?.totalPages || 1);

            content.forEach((identityUser) => {
              const id = identityUser?.id;
              if (!id) return;
              identityTimestampsCacheRef.current.set(id, extractIdentityTimestamps(identityUser));
            });

            page += 1;
          }

          identityListFetchRef.current.fetchedAll = true;
        } catch {
          // If the list endpoint fails (permissions, etc.), we fallback to per-id fetch.
        }
      })().finally(() => {
        identityListFetchRef.current.inFlight = null;
      });

      await identityListFetchRef.current.inFlight;
    };

    await prefetchFromIdentityList();

    const idsToFetch = validUsers
      .filter((u) => !u.createdAt)
      .map((u) => u.id)
      .filter(Boolean)
      .filter((id) => !identityTimestampsCacheRef.current.has(id));

    const concurrency = 5;
    const inFlight = new Set();
    const queue = [...idsToFetch];

    const runNext = async () => {
      const id = queue.shift();
      if (!id) return;
      const p = (async () => {
        try {
          const res = await apiClient.get(`/identity-service/api/v1/users/${id}`);
          identityTimestampsCacheRef.current.set(
            id,
            extractIdentityTimestamps(res.data),
          );
        } catch {
          // Cache miss to avoid retry storms if identity is unstable.
          identityTimestampsCacheRef.current.set(id, null);
        }
      })().finally(() => inFlight.delete(p));
      inFlight.add(p);
      if (inFlight.size >= concurrency) {
        await Promise.race(inFlight);
      }
      await runNext();
    };

    await runNext();
    await Promise.allSettled([...inFlight]);

    return userList.map((u) => {
      const identity = identityTimestampsCacheRef.current.get(u?.id);
      if (!identity) return u;
      return {
        ...u,
        createdAt: identity.createdAt || u.createdAt,
        updatedAt: identity.updatedAt || u.updatedAt,
      };
    });
  }, []);

  const getRequest = useCallback(async (url) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const getOptionalRequest = useCallback(async (url, fallbackValue = null) => {
    try {
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      if ([403, 404].includes(err?.response?.status)) {
        return fallbackValue;
      }
      throw err;
    }
  }, []);

  const postRequest = useCallback(async (url, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(url, payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const putRequest = useCallback(async (url, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.put(url, payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const patchRequest = useCallback(async (url, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.patch(url, payload);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteRequest = useCallback(async (url) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.delete(url);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteRequestWithoutLoading = useCallback(async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (err) {
      throw err;
    }
  }, []);

//   /** -------------------- Fetch Users with Pagination -------------------- **/


  const [allUsersForStats, setAllUsersForStats] = useState([]);


  const searchUserProfiles = useCallback(
  async (query = "", status = "ALL", searchPage = 0) => {
    if (!authToken) return;

    const BASE_URL = `/profile-service/api/v1/user-profiles`;

    try {
      setLoading(true);
      setError(null);

      const trimmedQuery = query?.trim() || "";
      const hasQuery = trimmedQuery.length >= 3;
      const hasStatus = status && status !== "ALL";
      const hasStaff = staffFilter !== null;

      const params = new URLSearchParams({
        page: String(searchPage),
        size: String(pageSize),
      });

      if (hasQuery) {
        params.append("q", trimmedQuery);
      }

      if (hasStatus) {
        params.append("status", status);
      }

      if (hasStaff) {
        params.append("staff", String(staffFilter));
      }

      const endpoint =
        hasQuery || hasStatus || hasStaff
          ? `${BASE_URL}/search`
          : BASE_URL;

      const response = await apiClient.get(
        `${endpoint}?${params.toString()}`
      );

      const {
        content = [],
        totalPages = 1,
        totalElements = 0,
      } = response.data;

      const organizedUsers = content.map(formatUserData);

      const enrichedUsers = await enrichUsersWithIdentityTimestamps(organizedUsers);
      setUsers(enrichedUsers);
      setTotalPages(totalPages);
      setTotalElements(totalElements);

      return enrichedUsers;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to search users. Please try again later.";

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  },
  [authToken, enrichUsersWithIdentityTimestamps, pageSize, staffFilter],
);

  const getUserById = useCallback(
    async (userId) => {
      if (!userId) throw new Error("User ID is required");

      const profileUrl = staffFilter
        ? `/profile-service/api/v1/user-profiles/${userId}?refresh=true&staff=true`
        : `/profile-service/api/v1/user-profiles/${userId}?refresh=true`;

      const [profileData, identityData, helperProfileData] = await Promise.all([
        getRequest(profileUrl),
        getRequest(`/identity-service/api/v1/users/${userId}`),
        getOptionalRequest(`/olitask-service/api/v1/helper-profiles/${userId}`),
      ]);

      const helperProfile =
        helperProfileData?.content && !Array.isArray(helperProfileData?.content)
          ? helperProfileData.content
          : helperProfileData;

      const identityUser = unwrapContentObject(identityData);

      const mergedData = {
        ...profileData,
        statusChangeNotes:
          identityUser?.statusChangeNotes || profileData?.statusChangeNotes || null,
        createdAt:
          identityUser?.createdAt ||
          identityUser?.createdOn ||
          identityUser?.insertedAt ||
          profileData?.createdAt ||
          null,
        updatedAt:
          identityUser?.updatedAt ||
          identityUser?.updatedOn ||
          identityUser?.modifiedAt ||
          identityUser?.lastUpdatedAt ||
          profileData?.updatedAt ||
          null,
        helperProfile: helperProfile || null,
        helperProfileStatus:
          helperProfile?.helperStatus ||
          helperProfile?.status ||
          profileData?.helperProfileStatus ||
          null,
        helperStatusChangeNotes:
          helperProfile?.statusNotes ||
          helperProfile?.statusChangeNotes ||
          null,
      };
      return formatUserData(mergedData);
    },
    [getOptionalRequest, getRequest, staffFilter],
  );

  const getHelperProfileByUserId = useCallback(
    async (userId) => {
      if (!userId) throw new Error("User ID is required");
      const data = await getOptionalRequest(
        `/olitask-service/api/v1/helper-profiles/${userId}`,
      );
      if (!data) return null;
      if (data?.content && !Array.isArray(data.content)) return data.content;
      return data;
    },
    [getOptionalRequest],
  );

  /** -------------------- CRUD & Verification -------------------- **/

  const createUser = async (userData, profileData) => {
    const user = await postRequest(
      "/identity-service/api/v1/auth/signup",
      userData,
    );

    if (!user?.id) {
      throw new Error("User ID not returned from signup");
    }

    const profilePayload = buildProfilePayload(user, userData, profileData);
    const profileUrl =
      staffFilter === null
        ? "/profile-service/api/v1/user-profiles"
        : `/profile-service/api/v1/user-profiles?staff=${staffFilter}`;
    const profile = await postRequest(
      profileUrl,
      profilePayload,
    );

    // Some backends only "promote" a newly-created staff profile into the staff index
    // after the user/profile is refreshed. Without this, a staff-created user can show
    // up under customers until the details page is opened (which calls refresh=true).
    // Best-effort refresh here to keep tables consistent immediately after creation.
    const staffRefreshParam =
      staffFilter === true ? "&staff=true" : "";
    await Promise.allSettled([
      apiClient.get(
        `/profile-service/api/v1/user-profiles/${user.id}?refresh=true${staffRefreshParam}`,
      ),
      apiClient.get(`/identity-service/api/v1/users/${user.id}`),
    ]);

    return { success: true, user, profile };
  };

  const patchIdentityProperties = (userId, propertiesArray) =>
    patchRequest(
      `/identity-service/api/v1/users/props?uid=${userId}`,
      propertiesArray,
    );

  const patchProfileProperties = (userId, propertiesArray) =>
    patchRequest(
      `/profile-service/api/v1/user-profiles/props?uid=${userId}`,
      propertiesArray,
    );

  const verifyUserIdentity = (userId) =>
    postRequest(
      `/profile-service/api/v1/user-profiles/identity-verification/user/${userId}/verify`,
      {},
    );

  const verifyWorkAuthorization = (userId) =>
    postRequest(
      `/profile-service/api/v1/user-profiles/work-authorization/user/${userId}/verify`,
      {},
    );

  // Verify/unverify the main user profile
  const verifyUserProfile = (userId, verified = true) =>
    patchProfileProperties(userId, [{ op: 'replace', path: 'verified', value: verified }]);

  // Verify security photo
  const verifySecurityPhoto = (userId) =>
    postRequest(
      `/profile-service/api/v1/user-profiles/security-photo/user/${userId}/verify`,
      {},
    );

  const updateHelperProfileStatus = (userId, status, notes = null) => {
    if (!userId) throw new Error("User ID is required");
    if (!status) throw new Error("Helper profile status is required");

    return putRequest(
      `/olitask-service/api/v1/helper-profiles/${userId}/status`,
      {
        helperId: userId,
        status,
        notes,
      },
    );
  };

  const deleteUserProfileProperty = (userId) => {
    if (!authToken) throw new Error("Missing auth token");
    if (!user?.id) throw new Error("Missing actor id");
    return deleteRequestWithoutLoading(
      `/profile-service/api/v1/user-profiles?id=${userId}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "actor-id": user.id,
          "actor-id-type": "USER",
        },
      },
    );
  };


  /** 1. Unified Fetch  **/
  const fetchAllUsers = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError(null);

    try {
      const BASE_URL = "/profile-service/api/v1/user-profiles";

      // Request 1: The Paginated Page for the Table
      const tableParams = { 
        page, 
        size: pageSize, 
        refresh: "true",
        ...(staffFilter !== null && { staff: String(staffFilter) })
      };

  // Stats fetch
const statsParams = { 
  page: 0,
  size: 10000,  // fetch all users
  ...(staffFilter !== null && { staff: String(staffFilter) })
};

      const requests = [apiClient.get(BASE_URL, { params: tableParams })];
      if (includeStats) {
        requests.push(apiClient.get(BASE_URL, { params: statsParams }));
      }

      const [tableRes, statsRes] = await Promise.all(requests);

      // Handle Table Data (Defensive Check like Health Check example)
      const tableData = tableRes.data;
      let normalizedTableUsers = [];
      if (tableData.content) {
        normalizedTableUsers = tableData.content.map(formatUserData);
        setUsers(await enrichUsersWithIdentityTimestamps(normalizedTableUsers));
        setTotalPages(tableData.totalPages);
        setTotalElements(tableData.totalElements);
      } else {
        const list = Array.isArray(tableData) ? tableData : [];
        normalizedTableUsers = list.map(formatUserData);
        setUsers(await enrichUsersWithIdentityTimestamps(normalizedTableUsers));
        setTotalPages(1);
        setTotalElements(list.length);
      }

      if (includeStats) {
        // Handle Full Stats Data
        const statsData = statsRes?.data;
        const fullList = statsData?.content || (Array.isArray(statsData) ? statsData : []);
        setAllUsersForStats(fullList.map(formatUserData));
      } else {
        setAllUsersForStats(normalizedTableUsers);
      }

    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load users";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [
    authToken,
    enrichUsersWithIdentityTimestamps,
    includeStats,
    page,
    pageSize,
    staffFilter,
  ]);

  /** 2. Triggers **/
  useEffect(() => {
    if (!autoFetch) return undefined;
    fetchAllUsers();
    return undefined;
  }, [autoFetch, fetchAllUsers]);

  /** 3. Refetch (What UI calls) **/
  const refetchAll = useCallback(() => {
    fetchAllUsers();
  }, [fetchAllUsers]);

  const getStats = useCallback(() => {
  if (!allUsersForStats || allUsersForStats.length === 0) return {};

  return allUsersForStats.reduce((acc, u) => {
    const status = u.status || "UNKNOWN";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
}, [allUsersForStats]);

const stats = useMemo(() => getStats(), [getStats]);


  /** -------------------- Return Object -------------------- **/

  return {
    users,
    loading,
    error,
    totalPages,
    totalElements,
    refetch: refetchAll,
    totalUsers: allUsersForStats.length || totalElements,
    activeUsers: allUsersForStats.filter((u) => u.status === "ACTIVE").length,
    verifiedUsers: allUsersForStats.filter((u) => u.verified).length,

    // Single user management
    getUserById,
    getHelperProfileByUserId,
    createUser,
    patchUserProperties: patchIdentityProperties,
    patchProfileProperties,
    verifyUserIdentity,
    verifyWorkAuthorization,
    verifyUserProfile,
    verifySecurityPhoto,
    updateHelperProfileStatus,
    deleteUserProfileProperty,
    searchUserProfiles,

    // getStats,

    allUsersForStats,

    // Staff filter
    staffFilter,
    setStaffFilter,
  };
};

/** -------------------- Helpers -------------------- **/

function formatUserData(user = {}) {
  const email =
    user.email ||
    user.contactDetails?.personalEmail ||
    user.contactDetails?.workEmail ||
    null;
  const phone =
    user.phone ||
    user.contactDetails?.mobilePhone ||
    user.contactDetails?.homePhone ||
    user.contactDetails?.workPhone ||
    null;

  return {
    id: user.id || "None",
    username: user.username || null,
    createdAt: user.createdAt || user.createdOn || null,
    updatedAt: user.updatedAt || null,
    email: email || "None",
    phone: phone || "None",
    firstName: user.firstName || "None",
    middleName: user.middleName || "",
    lastName: user.lastName || "None",
    gender: user.gender || null,
    fullName:
      `${user.firstName || ""} ${user.middleName || ""} ${
        user.lastName || ""
      }`.trim() || "None",
    profilePhoto: user.profilePhoto || null,
    birthDate: user.birthDate || null,
    ssn: user.ssn || null,
    taxIdType: user.taxIdType || null,
    taxId: user.taxId || null,
    securityPhoto: user.securityPhoto || null,
    verified: Boolean(user.verified),
    status: user.status || "UNKNOWN",
    statusChangeNotes: user.statusChangeNotes || null,
    userIdentityVerification: user.userIdentityVerification || "pending",
    userWorkAuthorization: user.userWorkAuthorization || "None",
    completeProfile: computeProfileCompleteness(user),

    permanentAddress: user.permanentAddress || null,
    residentialAddress: user.residentialAddress || null,
    workAddress: user.workAddress || null,
    schoolAddress: user.schoolAddress || null,
    billingAddress: user.billingAddress || null,

    preferredMailingAddress: user.preferredMailingAddress || null,
    preferredContactMethod: user.preferredContactMethod || null,
    helperProfile: user.helperProfile || null,
    helperProfileStatus:
      user.helperProfileStatus ||
      user.helperProfile?.helperStatus ||
      user.helperProfile?.status ||
      null,
    helperApprovedAt:
      user.helperApprovedAt || user.helperProfile?.approvedAt || null,
    helperRejectedAt:
      user.helperRejectedAt || user.helperProfile?.rejectedAt || null,
    helperCreatedAt: user.helperCreatedAt || user.helperProfile?.createdAt || null,
    helperUpdatedAt:
      user.helperUpdatedAt ||
      user.helperProfile?.updatedAt ||
      user.helperProfile?.signedUpAt ||
      null,
    helperStatusChangeNotes:
      user.helperStatusChangeNotes ||
      user.helperProfile?.statusNotes ||
      user.helperProfile?.statusChangeNotes ||
      user.helperProfile?.notes ||
      null,
  };
}

function computeProfileCompleteness(user) {
  const required = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "profilePhoto",
    "securityPhoto",
  ];
  const filled = required.filter((key) => user[key]);
  return Math.round((filled.length / required.length) * 100);
}

function buildProfilePayload(user, userData, profileData) {
  const contactDetails = {
    mobilePhone:
      profileData.contactDetails?.mobilePhone || userData.phone || "",
    homePhone: profileData.contactDetails?.homePhone || "",
    workPhone: profileData.contactDetails?.workPhone || "",
    personalEmail:
      profileData.contactDetails?.personalEmail || userData.email || "",
    email:
      profileData.contactDetails?.email ||
      profileData.workEmail ||
      userData.email ||
      "",
    website: profileData.contactDetails?.website || "",
    twitter: profileData.contactDetails?.twitter || "",
    facebook: profileData.contactDetails?.facebook || "",
    linkedin: profileData.contactDetails?.linkedin || "",
  };

  const buildAddress = (prefix) => ({
    street: profileData[`${prefix}Address`]?.street || "",
    city: profileData[`${prefix}Address`]?.city || "",
    state: profileData[`${prefix}Address`]?.state || "",
    zipCode: profileData[`${prefix}Address`]?.zipCode || "",
    country: profileData[`${prefix}Address`]?.country || "",
  });

  const permanentAddress = buildAddress("permanent");
  const residentialAddress = buildAddress("residential");
  const workAddress = buildAddress("work");
  const schoolAddress = buildAddress("school");
  const billingAddress = buildAddress("billing");

  const payload = {
    id: user.id,
    firstName: userData.firstName,
    middleName: userData.middleName || "",
    lastName: userData.lastName,
    username: userData.username,
    profilePhoto: profileData.profilePhoto || null,
    birthDate: profileData.birthDate || "",
    gender: profileData.gender ? profileData.gender.toUpperCase() : null,
    ssn: profileData.ssn || null,
    taxIdType: profileData.taxIdType || null,
    taxId: profileData.taxId || null,
    status: profileData.status || userData.status || "ACTIVE",
    nextOfKin: profileData.nextOfKin || null,
    identificationDocumentType: profileData.identificationDocumentType || null,
    userWorkAuthorization: profileData.userWorkAuthorization || null,
    contactDetails,
    permanentAddress,
    residentialAddress,
    workAddress,
    schoolAddress,
    billingAddress,
    preferredMailingAddress: profileData.preferredMailingAddress || null,
    preferredContactMethod: profileData.preferredContactMethod || null,
  };

  const cleanPayload = Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (value === null || value === "") return false;
      if (typeof value === "object" && !Array.isArray(value)) {
        if (
          [
            "permanentAddress",
            "residentialAddress",
            "workAddress",
            "schoolAddress",
            "billingAddress",
          ].includes(key)
        ) {
          return true;
        }
        return Object.values(value).some((val) => val !== null && val !== "");
      }
      return true;
    }),
  );

  return cleanPayload;
}

export default useUsers;
